import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { TransferJob } from '@/types/transfer-job';

// Atom with localStorage persistence
export const transferJobsAtom = atomWithStorage<TransferJob[]>(
  'd9-cross-chain-transfer-jobs',
  [],
);

export const sortedJobsAtom = atom((get) => {
  const jobs = get(transferJobsAtom);
  return jobs.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
});

// Derived atoms for filtered views
export const activeJobsAtom = atom((get) => {
  const jobs = get(transferJobsAtom);
  return jobs
    .filter((job) => job.status === 'active' || job.status === 'pending')
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
});

export const completedJobsAtom = atom((get) => {
  const jobs = get(transferJobsAtom);
  return jobs
    .filter((job) => job.status === 'completed')
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
});

export const failedJobsAtom = atom((get) => {
  const jobs = get(transferJobsAtom);
  return jobs
    .filter((job) => job.status === 'failed')
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
});

// Utility atoms for job management
export const addTransferJobAtom = atom(
  null,
  (get, set, job: Omit<TransferJob, 'createdAt'>) => {
    const jobs = get(transferJobsAtom);
    const newJob: TransferJob = {
      ...job,
      createdAt: new Date(),
    };
    set(transferJobsAtom, [...jobs, newJob]);
  },
);

export const updateTransferJobAtom = atom(
  null,
  (
    get,
    set,
    { jobId, updates }: { jobId: string; updates: Partial<TransferJob> },
  ) => {
    const jobs = get(transferJobsAtom);
    const updatedJobs = jobs.map((job) =>
      job.jobId === jobId
        ? {
            ...job,
            ...updates,
            ...(updates.status === 'completed' || updates.status === 'failed'
              ? { completedAt: new Date() }
              : {}),
          }
        : job,
    );
    set(transferJobsAtom, updatedJobs);
  },
);

export const clearTransferJobsAtom = atom(null, (get, set) => {
  set(transferJobsAtom, []);
});

export const getTransferJobAtom = atom((get) => (jobId: string) => {
  const jobs = get(transferJobsAtom);
  return jobs.find((job) => job.jobId === jobId);
});

export const deleteTransferJobAtom = atom(null, (get, set, jobId: string) => {
  const jobs = get(transferJobsAtom);
  const updatedJobs = jobs.filter((job) => job.jobId !== jobId);
  set(transferJobsAtom, updatedJobs);
});
